import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation, Trans } from 'react-i18next';
import { Container, Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';

import useToast from '@yaydoo/react-components/lib/hooks/useToast';

import GuestLayout from '@layouts/guest';
import useStyles from '@containers/checkout/styles';
import Ticket from '@components/ticket';
import useStore from '@hooks/useStore';
import PaymentForm from './payment-form';
import GenericSpeiInfo from '@containers/checkout/payment-form/SpeiPaymentForm/GenericSpeiInfo';
import PaymentSuccess from '@containers/checkout/payment-success';
import CashReference from '@containers/checkout/cash-reference';
import { CheckoutProps, CheckoutRouteParams } from '@containers/types';
import {
  PaymentLinkRedirectParams,
  PaymentLinkRedirectStatus,
  PaymentLinkStatus,
} from '@constants/paymentLink';
import { paymentsLanding } from '@configs/variables';
import { sendEvent } from '@utils/ga';
import { ACTIONS, CATEGORIES } from '@constants/analytics';
import getUrlParam from '@utils/getUrlParam';
import { initOpenPay } from '@utils/openPay';
import { formatPhoneNumber } from '@utils/format';
import handleRedirect from '@utils/handleRedirect';
import { ReactComponent as EmailIcon } from '@assets/images/icons/email.svg';
import { ReactComponent as PhoneIcon } from '@assets/images/icons/phone.svg';
import { isProd } from '@configs/variables';

interface CheckoutContent {
  isPaymentSuccess: boolean;
  isCashReference: boolean;
  isSpeiReference: boolean;
}

const getCheckoutContent = ({
  isPaymentSuccess,
  isCashReference,
  isSpeiReference,
}: CheckoutContent) => {
  if (isPaymentSuccess) return PaymentSuccess;
  if (isCashReference) return CashReference;
  if (isSpeiReference) return GenericSpeiInfo;
  return PaymentForm;
};

const Checkout: FC<CheckoutProps> = () => {
  const { t } = useTranslation();
  const { links, kushki, checkout, dlocal, arcus, fintoc, monato } = useStore();
  const { secondaryColor, phone, email, favicon } = links.coBrandingConfig;
  const { paymentMethods } = links.paymentConfig;
  const { businessName } = links.accountInfo;
  const css = useStyles({ secondaryColor });
  const { openToast } = useToast();
  const params = useParams<CheckoutRouteParams>();

  const linkId = params.uuid;
  const isPaymentSuccess =
    checkout.success.cardPayment ||
    links.paymentLink.isPaid ||
    links.paymentLink.isSubscribed ||
    links.paymentLink.status === PaymentLinkStatus.PENDING_ACTIVATION ||
    checkout.success.directDebitPayment ||
    links.paymentLink.status === PaymentLinkStatus.SUBSCRIPTION_CANCELED;

  const isCashReference = checkout.success.cashPayment;
  const isSpeiReference =
    dlocal.flags.isSpeiReferenceGenerated ||
    arcus.flags.isSpeiReferenceGenerated ||
    fintoc.flags.isSpeiReferenceGenerated ||
    monato.flags.isSpeiReferenceGenerated;
  const userFullName = links.accountInfo.businessName;
  const ticketNumber = links.paymentLink.linkNumberFormatted;
  const ticketItems = links.paymentLink.linkItems;
  const status = links.paymentLink.displayStatus;
  const isLoading = links.flags.isRequesting || !links.paymentLink.exists;
  const paymentDate = links.paymentLink.lastPaidAt;
  const hideTo = links.paymentLink.isMultiple;
  const hasEmail = Boolean(email);
  const hasPhone = Boolean(phone);

  const Content = getCheckoutContent({
    isPaymentSuccess,
    isCashReference,
    isSpeiReference,
  });

  const handleStatus = () => {
    switch (getUrlParam(PaymentLinkRedirectParams.STATUS)) {
      case PaymentLinkRedirectStatus.SUCCESS: {
        links.setAsPaid();
        break;
      }
      case PaymentLinkRedirectStatus.ERROR: {
        openToast({
          type: 'error',
          title: t('checkoutScreen.payment.errors.card.title'),
          message: t('checkoutScreen.payment.errors.card.confirmation'),
        });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (paymentMethods && paymentMethods.card.isPartnerOpenPay) {
      initOpenPay({
        apiKey: paymentMethods.card.publicApiKey,
        id: paymentMethods.card.merchantId,
        isProd,
      });
    }
  }, [paymentMethods.card.publicApiKey, paymentMethods.card.merchantId]);

  useEffect(() => {
    if (favicon) {
      const favIcon = document.getElementById('favicon') as HTMLLinkElement;
      if (favIcon) {
        favIcon.href = favicon;
      }
    }
  }, [favicon]);

  useEffect(() => {
    if (businessName) document.title = `${businessName} - ${t('common.oysterCheckout')}`;
  }, [businessName]);

  useEffect(() => {
    links.getPaymentLink(linkId).then(() => {
      handleStatus();
    });
  }, [linkId]);

  useEffect(() => {
    if (checkout.success.cardPayment) {
      links.setAsPaid();
    }
  }, [checkout.success.cardPayment]);

  const onToggleList = (open: boolean) => {
    if (open) {
      sendEvent({
        category: CATEGORIES.CHECKOUT.CATEGORY,
        action: ACTIONS.CLICK,
        label: CATEGORIES.CHECKOUT.LABELS.VIEW_LINK_DETAILS,
      });
    }
  };

  useEffect(() => {
    if (isPaymentSuccess || isCashReference) {
      const cashData = {
        cashReference: kushki.cashReferenceNumber,
        barcodeUrl: kushki.barcodeUrl,
      };
      const payload = isCashReference ? cashData : null;
      const isRedirecting = handleRedirect(payload);
      if (!isRedirecting && isPaymentSuccess) links.setAsPaid();
    }
  }, [isPaymentSuccess, isCashReference]);

  return (
    <GuestLayout loading={isLoading}>
      <Container className={css.root}>
        <Grid
          container
          justifyContent="space-around"
          alignContent="flex-start"
          className={isPaymentSuccess ? css.paymentSuccessContainer : undefined}
        >
          <Grid item sm={6} data-testid="detail-section" className={css.detailSection}>
            <Ticket
              details={{
                ...links.paymentLink,
                userFullName,
                ticketNumber,
                ticketItems,
                paymentDate,
                status,
              }}
              hideTo={hideTo}
              onToggleList={onToggleList}
            />
            {(hasEmail || hasPhone) && (
              <div className={css.contactsContainer}>
                <div className={css.customerInfoTitle}>
                  <span>{t('checkoutScreen.customerContactDetails.title')}</span>
                </div>
                {hasEmail && (
                  <Grid container alignItems="center" className={css.contactInfoRow}>
                    <Grid item xs={1}>
                      <EmailIcon />
                    </Grid>
                    <Grid item xs={11} className={css.customerInfoDesc}>
                      {email}
                    </Grid>
                  </Grid>
                )}
                {hasPhone && (
                  <Grid container alignItems="center" className={css.contactInfoRow}>
                    <Grid item xs={1}>
                      <PhoneIcon />
                    </Grid>
                    <Grid item xs={11} className={css.customerInfoDesc}>
                      {formatPhoneNumber(phone)}
                    </Grid>
                  </Grid>
                )}
              </div>
            )}
            <div className={css.useOyster}>
              <Trans i18nKey="common.messages.useOyster">
                <a
                  data-testid="action-link"
                  href={paymentsLanding}
                  target="_blank"
                  rel="noreferrer noopener"
                />
              </Trans>
            </div>
          </Grid>

          <Grid item sm={6} data-testid="payment-section" className={css.paymentSection}>
            <Content />
          </Grid>
        </Grid>
      </Container>
    </GuestLayout>
  );
};

export default observer(Checkout);
